import React from 'react';
import { Admin, Resource, CustomRoutes } from 'react-admin';
import { Route, BrowserRouter } from 'react-router-dom';
import { authProvider } from './auth/authProvider';
import { LoginPage } from './auth/LoginPage';
import { dataProvider } from './dataProvider';
import { UserList } from './users/UserList';
import { UserEdit } from './users/UserEdit';
import { OrgChart } from './users/OrgChart';
import Home from './pages/dashboard/Home';
import { ApplyTimeoff } from './timeoff/ApplyTimeoff';
import { TimeoffList } from './timeoff/TimeoffList';
import { TimeoffApprovalList } from './timeoff/TimeoffApprovalList';
import { TimeoffBalance } from './timeoff/TimeoffBalance';
import { AllRequestsList } from './timeoff/AllRequestsList';
import { HotlinkApproval } from './timeoff/HotlinkApproval';
import { WorkScheduleList } from './work-schedules/WorkScheduleList';
import { WorkScheduleCreate } from './work-schedules/WorkScheduleCreate';
import { WorkScheduleEdit } from './work-schedules/WorkScheduleEdit';
import { OvertimeApply } from './overtime/OvertimeApply';
import { OvertimeList } from './overtime/OvertimeList';
import { OvertimeAdminList } from './overtime/OvertimeAdminList';
import { NightShiftDeclarationList } from './night-shift-declarations/NightShiftDeclarationList';
import { ApplyNightShiftDeclaration } from './night-shift-declarations/ApplyNightShiftDeclaration';
import { HolidayList } from './holidays/HolidayList';
import { EmployeeList } from './employees/EmployeeList';
import { EmployeeShow } from './employees/EmployeeShow';
import { EmployeeEdit } from './employees/EmployeeEdit';
import { TerminationChecklistList } from './terminations/TerminationChecklistList';
import { TerminationChecklistShow } from './terminations/TerminationChecklistShow';
import { HotlinkConfirmPage } from './terminations/HotlinkConfirmPage';
import { TimesheetGrid } from './timesheets/TimesheetGrid';

export const App: React.FC = () => {
    return (
        <BrowserRouter>
            <Admin
                authProvider={authProvider}
                dataProvider={dataProvider}
                loginPage={LoginPage}
                requireAuth
                dashboard={Home}
            >
                {/* Public hotlink routes – no layout, no auth check */}
                <CustomRoutes noLayout>
                    <Route path="/hotlink/approval/:token" element={<HotlinkApproval />} />
                    <Route path="/terminations/hotlink/confirm" element={<HotlinkConfirmPage />} />
                </CustomRoutes>
                {(permissions: string[]) => (
                    <>
                        <CustomRoutes noLayout>
                            <Route path="/hotlink/approval/:token" element={<HotlinkApproval />} />
                        </CustomRoutes>
                        <CustomRoutes>
                            <Route path="/" element={<Home />} />
                        </CustomRoutes>
                        <Resource
                            name="timeoff"
                            list={permissions.includes('timeoff.read') ? TimeoffList : undefined}
                            create={ApplyTimeoff}
                        >
                            <Route path="approvals" element={<TimeoffApprovalList />} />
                            <Route path="balances" element={<TimeoffBalance />} />
                            <Route path="all-requests" element={<AllRequestsList />} />
                        </Resource>
                        
                        <Resource
                            name="overtime"
                            list={permissions.includes('overtime.read') ? OvertimeList : undefined}
                            create={permissions.includes('overtime.create') ? OvertimeApply : undefined}
                        >
                            {permissions.includes('overtime.view_all') && (
                                <Route path="all-declarations" element={<OvertimeAdminList />} />
                            )}
                        </Resource>
                        
                        <Resource
                            name="night-shift-declarations"
                            list={permissions.includes('night-shift-declarations.read') ? NightShiftDeclarationList : undefined}
                            create={ApplyNightShiftDeclaration}
                        />
                        <Resource
                            name="users"
                            list={permissions.includes('users.read') ? UserList : undefined}
                            edit={UserEdit}
                        >
                            <Route path="org-chart" element={<OrgChart />} />
                        </Resource>

                        <Resource
                            name="employees"
                            list={permissions.includes('employees.read') ? EmployeeList : undefined}
                            show={permissions.includes('employees.read') ? EmployeeShow : undefined}
                            edit={permissions.includes('employees.manage') ? EmployeeEdit : undefined}
                        />
                        <Resource
                            name="work-schedules"
                            list={permissions.includes('work-schedules.read') ? WorkScheduleList : undefined}
                            create={WorkScheduleCreate}
                            edit={WorkScheduleEdit}
                        />
                        <Resource
                            name="holidays"
                            list={HolidayList}
                        />
                        <Resource
                            name="terminations"
                            list={permissions.includes('terminations.read') ? TerminationChecklistList : undefined}
                            show={permissions.includes('terminations.read') ? TerminationChecklistShow : undefined}
                        />
                        <Resource
                            name="timesheets"
                            list={permissions.includes('timesheets.manage') ? TimesheetGrid : undefined}
                        />
                    </>
                )}
            </Admin>
        </BrowserRouter>
    );
};
